/**
 * useProductSearch Hook
 * Debounced search for CARA products (PackagedMedicinalProduct and
 * PharmaceuticalProduct) from Neo4j graph, with "Load More" pagination.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BRRApiClient } from '../services';
import { EnrichedProduct } from '../services/types';

export interface UseProductSearchOptions {
  apiClient: BRRApiClient;
  query: string;
  debounceMs?: number;
  minQueryLength?: number;
  limit?: number;
}

export interface UseProductSearchReturn {
  results: EnrichedProduct[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  loadingMore: boolean;
}

export function useProductSearch({
  apiClient,
  query,
  debounceMs = 500,
  minQueryLength = 2,
  limit = 20,
}: UseProductSearchOptions): UseProductSearchReturn {
  const [results, setResults] = useState<EnrichedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(
    async (searchQuery: string) => {
      try {
        const response = await apiClient.searchProducts(searchQuery, limit);
        setResults(response.results);
        setHasMore(response.hasMore);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setResults([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [apiClient, limit]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || query.length < minQueryLength) return;
    setLoadingMore(true);
    try {
      const response = await apiClient.searchProducts(query, limit, results.length);
      setResults(prev => [...prev, ...response.results]);
      setHasMore(response.hasMore);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
    } finally {
      setLoadingMore(false);
    }
  }, [apiClient, query, limit, results.length, loadingMore, hasMore, minQueryLength]);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }

    if (query.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      setError(null);
      setHasMore(false);
      return;
    }

    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(query);
    }, debounceMs);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength, search]);

  return { results, loading, error, hasMore, loadMore, loadingMore };
}
